import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { impactApi, type ImpactAction, type ImpactVerdict } from '../api/impact'
import { useAuth } from '../contexts/AuthContext'
import { categoryLabel, formatDateBR } from '../lib/format'
import { Spinner } from '../components/Spinner'

type Win = '7' | '14'

const fmtNum = (n: number | null): string =>
  n == null ? '—' : n.toLocaleString('pt-BR', { maximumFractionDigits: Math.abs(n) >= 100 ? 0 : 2 })

interface Agg {
  total: number
  measured: number
  improved: number
  worsened: number
  flat: number
  pending: number
  pctImproved: number | null
}

function aggregate(rows: ImpactAction[], win: Win): Agg {
  let improved = 0
  let worsened = 0
  let flat = 0
  let pending = 0
  for (const r of rows) {
    const v: ImpactVerdict = win === '7' ? r.verdict7 : r.verdict14
    if (v === 'improved') improved++
    else if (v === 'worsened') worsened++
    else if (v === 'flat') flat++
    else pending++
  }
  const measured = improved + worsened + flat
  return {
    total: rows.length,
    measured,
    improved,
    worsened,
    flat,
    pending,
    pctImproved: measured ? Math.round((improved / measured) * 100) : null,
  }
}

const selCls =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none'

export default function Impact() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [win, setWin] = useState<Win>('7')
  // Escopo (Gerente/Cliente/Semana afetam a página toda) + Resultado (só a lista).
  const [manager, setManager] = useState('')
  const [client, setClient] = useState('')
  const [week, setWeek] = useState('')
  const [result, setResult] = useState<'' | ImpactVerdict>('')

  const { data, isLoading } = useQuery({ queryKey: ['impact'], queryFn: () => impactApi.list() })
  const rows = data?.actions ?? []

  // Opções dos filtros, derivadas dos próprios dados.
  const managerOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) m.set(r.managerId, r.managerName)
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])
  const clientOptions = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) m.set(r.clientId, r.clientName)
    return [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])
  const weekOptions = useMemo(
    () => [...new Set(rows.map((r) => r.weekStart))].sort((a, b) => b.localeCompare(a)),
    [rows]
  )

  // Escopo: Gerente + Cliente + Semana → cards, quebras e lista.
  const scopedRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!manager || r.managerId === manager) &&
          (!client || r.clientId === client) &&
          (!week || r.weekStart === week)
      ),
    [rows, manager, client, week]
  )
  // Resultado → só a lista (usa o veredito da janela ativa).
  const tableRows = useMemo(
    () => scopedRows.filter((r) => !result || (win === '7' ? r.verdict7 : r.verdict14) === result),
    [scopedRows, result, win]
  )

  const overall = useMemo(() => aggregate(scopedRows, win), [scopedRows, win])

  const breakdown = (keyFn: (r: ImpactAction) => { id: string; name: string }) => {
    const groups = new Map<string, { name: string; rows: ImpactAction[] }>()
    for (const r of scopedRows) {
      const { id, name } = keyFn(r)
      let g = groups.get(id)
      if (!g) {
        g = { name, rows: [] }
        groups.set(id, g)
      }
      g.rows.push(r)
    }
    return [...groups.values()]
      .map((g) => ({ name: g.name, agg: aggregate(g.rows, win) }))
      .sort((a, b) => (b.agg.pctImproved ?? -1) - (a.agg.pctImproved ?? -1))
  }
  const byCategory = useMemo(
    () => breakdown((r) => ({ id: r.category, name: categoryLabel(r.category) })),
    [scopedRows, win]
  )
  const byManager = useMemo(
    () => breakdown((r) => ({ id: r.managerId, name: r.managerName })),
    [scopedRows, win]
  )
  const byClient = useMemo(
    () => breakdown((r) => ({ id: r.clientId, name: r.clientName })),
    [scopedRows, win]
  )

  const anyFilter = Boolean(manager || client || week || result)
  const clearFilters = () => {
    setManager('')
    setClient('')
    setWeek('')
    setResult('')
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Impacto das ações</h1>
        <div className="inline-flex rounded-lg border border-slate-300 p-0.5">
          {(['7', '14'] as Win[]).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                win === w ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {w === '7' ? '7 dias' : '14 dias'}
            </button>
          ))}
        </div>
      </div>

      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <strong>Correlação, não prova.</strong> Comparamos a métrica-alvo antes (semana da ação) e
        depois ({win === '7' ? '~7' : '~14'} dias). Vários fatores e ações ocorrem na mesma semana —
        use como sinal de direção, não como causa isolada.
      </p>

      {/* Filtros — Gerente/Cliente/Semana afetam tudo; Resultado filtra só a lista. */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        {isAdmin && (
          <FilterField label="Gerente">
            <select value={manager} onChange={(e) => setManager(e.target.value)} className={selCls}>
              <option value="">Todos os gerentes</option>
              {managerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </FilterField>
        )}
        <FilterField label="Cliente">
          <select value={client} onChange={(e) => setClient(e.target.value)} className={selCls}>
            <option value="">Todos os clientes</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Resultado">
          <select
            value={result}
            onChange={(e) => setResult(e.target.value as '' | ImpactVerdict)}
            className={selCls}
          >
            <option value="">Todos os resultados</option>
            <option value="improved">Melhorou</option>
            <option value="worsened">Piorou</option>
            <option value="flat">Estável</option>
            <option value="pending">Aguardando</option>
          </select>
        </FilterField>
        <FilterField label="Semana da ação">
          <select value={week} onChange={(e) => setWeek(e.target.value)} className={selCls}>
            <option value="">Todas as semanas</option>
            {weekOptions.map((w) => (
              <option key={w} value={w}>
                {formatDateBR(w)}
              </option>
            ))}
          </select>
        </FilterField>
        {anyFilter && (
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto self-end rounded-md px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhuma ação concluída para medir ainda.
        </p>
      ) : (
        <>
          {/* Placar (reflete os filtros de escopo) */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Ações feitas" value={String(overall.total)} hint="com status 'feito'" />
            <StatCard
              label={`% que melhorou (${win}d)`}
              value={overall.pctImproved == null ? '—' : `${overall.pctImproved}%`}
              hint={`${overall.improved}/${overall.measured} medidas`}
              accent="emerald"
            />
            <StatCard
              label="Melhorou / Piorou / Estável"
              value={`${overall.improved} / ${overall.worsened} / ${overall.flat}`}
              hint={`na janela de ${win} dias`}
            />
            <StatCard
              label="Aguardando"
              value={String(overall.pending)}
              hint="sem 'depois' disponível ainda"
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <StackedBar agg={overall} />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Breakdown title="Por categoria" items={byCategory} />
            <Breakdown title="Por gerente" items={byManager} />
            <Breakdown title="Por cliente" items={byClient} />
          </div>

          {/* Lista (também filtrada por Resultado) */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2 font-semibold">Cliente</th>
                  <th className="px-3 py-2 font-semibold">Semana</th>
                  <th className="px-3 py-2 font-semibold">Ação</th>
                  <th className="px-3 py-2 font-semibold">Métrica</th>
                  <th className="px-3 py-2 text-right font-semibold">Antes</th>
                  <th className="px-3 py-2 text-right font-semibold">7 dias</th>
                  <th className="px-3 py-2 text-right font-semibold">14 dias</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tableRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">
                      Nenhuma ação com esses filtros.
                    </td>
                  </tr>
                ) : (
                  tableRows.map((r) => (
                    <tr key={`${r.type}-${r.id}`} className="align-top">
                      <td className="px-3 py-2 font-medium text-slate-700">{r.clientName}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                        {formatDateBR(r.weekStart)}
                      </td>
                      <td className="max-w-xs px-3 py-2 text-slate-700">
                        <span className="line-clamp-2">{r.description}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                          {categoryLabel(r.category)}
                          <span
                            className={`rounded-full px-1.5 py-px text-[10px] font-semibold ${
                              r.source === 'ai'
                                ? 'bg-violet-100 text-violet-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                            title={
                              r.source === 'ai'
                                ? 'Métrica-alvo marcada pela IA'
                                : 'Métrica inferida pela categoria'
                            }
                          >
                            {r.source === 'ai' ? 'IA' : 'categoria'}
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        <span className="whitespace-nowrap">{r.metricLabel}</span>
                        {/* Onde foi medido — campanha/plataforma da ação ou o consolidado. */}
                        <span
                          className="mt-0.5 block text-[11px] text-slate-400"
                          title="Onde o impacto foi medido: na campanha da ação, na plataforma dela ou, sem nenhuma das duas nos dados, no consolidado Google+Meta"
                        >
                          {r.scopeLabel}
                        </span>
                        {r.note && <span className="mt-0.5 block text-[11px] text-amber-600">{r.note}</span>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                        {fmtNum(r.before)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <VerdictCell before={r.before} after={r.after7} verdict={r.verdict7} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <VerdictCell before={r.before} after={r.after14} verdict={r.verdict14} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-500">{label}</label>
      {children}
    </div>
  )
}

function StatCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string
  value: string
  hint?: string
  accent?: 'emerald'
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-bold ${accent === 'emerald' ? 'text-emerald-600' : 'text-slate-900'}`}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div>}
    </div>
  )
}

function StackedBar({ agg }: { agg: Agg }) {
  const total = agg.measured + agg.pending
  if (total === 0) return <p className="text-sm text-slate-400">Sem ações medidas.</p>
  const seg = (n: number, cls: string, label: string) =>
    n === 0 ? null : (
      <div
        className={`flex items-center justify-center ${cls}`}
        style={{ width: `${(n / total) * 100}%` }}
        title={`${label}: ${n}`}
      >
        {n / total > 0.08 ? n : ''}
      </div>
    )
  return (
    <div>
      <div className="flex h-6 w-full overflow-hidden rounded-md text-[11px] font-semibold text-white">
        {seg(agg.improved, 'bg-emerald-500', 'Melhorou')}
        {seg(agg.worsened, 'bg-rose-500', 'Piorou')}
        {seg(agg.flat, 'bg-slate-400', 'Estável')}
        {seg(agg.pending, 'bg-slate-200 text-slate-500', 'Aguardando')}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <Legend cls="bg-emerald-500" label={`Melhorou (${agg.improved})`} />
        <Legend cls="bg-rose-500" label={`Piorou (${agg.worsened})`} />
        <Legend cls="bg-slate-400" label={`Estável (${agg.flat})`} />
        <Legend cls="bg-slate-200" label={`Aguardando (${agg.pending})`} />
      </div>
    </div>
  )
}

function Legend({ cls, label }: { cls: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-sm ${cls}`} />
      {label}
    </span>
  )
}

function Breakdown({ title, items }: { title: string; items: Array<{ name: string; agg: Agg }> }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{title}</h2>
      <div className="space-y-2.5">
        {items.length === 0 && <p className="text-xs text-slate-400">Sem dados.</p>}
        {items.map((it) => (
          <div key={it.name}>
            <div className="mb-0.5 flex items-center justify-between text-xs">
              <span className="truncate font-medium text-slate-600">{it.name}</span>
              <span className="text-slate-400">
                {it.agg.pctImproved == null ? '—' : `${it.agg.pctImproved}%`}
                <span className="ml-1 text-slate-300">({it.agg.measured})</span>
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${it.agg.pctImproved ?? 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function VerdictCell({
  before,
  after,
  verdict,
}: {
  before: number | null
  after: number | null
  verdict: ImpactVerdict
}) {
  if (verdict === 'pending' || after == null) return <span className="text-slate-300">—</span>
  const pct = before ? Math.round(((after - before) / Math.abs(before)) * 100) : null
  const up = before != null ? after > before : false
  const cls =
    verdict === 'improved'
      ? 'text-emerald-700'
      : verdict === 'worsened'
        ? 'text-rose-700'
        : 'text-slate-500'
  const arrow = verdict === 'flat' ? '=' : up ? '↑' : '↓'
  return (
    <span className={`inline-flex items-center justify-end gap-1 ${cls}`}>
      {fmtNum(after)}
      <span className="text-[10px]">
        {arrow}
        {pct != null ? ` ${pct > 0 ? '+' : ''}${pct}%` : ''}
      </span>
    </span>
  )
}
